<template>
  <div class="select-wrapper">
    <Dropdown
      :model-value="selectedDropdownItem"
      :options="options"
      :option-label="optionLabel"
      :option-group-label="optionGroupLabelKey"
      :option-group-children="optionGroupChildrenKey"
      :placeholder="placeholder"
      :disabled="disabled"
      class="w-full col-12 p-0"
      @change="handleChange"
      @before-show="onBeforeShow"
    >
      <template #value="slotProps">
        <span v-if="slotProps.value">
          {{ slotProps.value[optionLabel] ? slotProps.value[optionLabel] : slotProps.value }}
        </span>
        <span v-else>
          {{ slotProps.placeholder }}
        </span>
      </template>
      <template #option="slotProps">
        <div class="base-dropdown-multiple-option">
          {{ slotProps.option[optionLabel] ? slotProps.option[optionLabel] : slotProps.option }}
        </div>
      </template>
      <template #group="slotProps">
        <div class="base-dropdown-multiple-group">
          <strong>{{ slotProps.option[optionGroupLabel] ? slotProps.option[optionGroupLabel] : slotProps.option }}</strong>
        </div>
      </template>
    </Dropdown>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
  computed,
} from 'vue';
import Dropdown, { DropdownChangeEvent } from 'primevue/dropdown';

interface SelectedValue {
  type: 'system' | 'custom';
  name: string;
}

interface DropdownItem {
  [key: string]: any;
}

interface DropdownGroup {
  [key: string]: any;
}

export default defineComponent({
  name: 'BaseDropdownMultiple',

  components: {
    Dropdown,
  },

  props: {
    modelValue: {
      type: Array as PropType<SelectedValue[]>,
      default: () => [],
    },

    options: {
      type: Array as PropType<DropdownGroup[]>,
      required: true,
    },

    optionLabel: {
      type: String,
      default: 'label',
    },

    optionValueKey: {
      type: String,
      default: 'value',
    },

    optionGroupLabel: {
      type: String,
      default: 'groupLabel',
    },

    optionGroupChildren: {
      type: String,
      default: 'items',
    },

    placeholder: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'change'],

  setup(props, { emit }) {
    const selectedDropdownItem = ref<DropdownItem | null>(null);

    const findOptionByItemValue = (
      valueToFind: SelectedValue | null,
      currentOptions: DropdownGroup[],
    ): DropdownItem | null => {
      if (!valueToFind) return null;

      const allItems = currentOptions.reduce((acc, group) => {
        const items = group[props.optionGroupChildren] as DropdownItem[] | undefined;
        if (items) {
          acc.push(...items);
        }
        return acc;
      }, [] as DropdownItem[]);

      return allItems.find(
        (item: DropdownItem) => JSON.stringify(item[props.optionValueKey]) === JSON.stringify(valueToFind),
      ) || null;
    };

    watch(
      () => [props.modelValue, props.options] as [SelectedValue[] | null, DropdownGroup[]],
      ([newModelValue, newOptions]: [SelectedValue[] | null, DropdownGroup[]]) => {
        const currentModelValue = newModelValue || [];
        const targetValue: SelectedValue | null = (currentModelValue.length > 0) ? currentModelValue[0] : null;

        if (targetValue) {
          selectedDropdownItem.value = findOptionByItemValue(targetValue, newOptions);
        } else {
          selectedDropdownItem.value = null;
        }
      },
      { immediate: true, deep: true },
    );

    const handleChange = (event: DropdownChangeEvent) => {
      const fullSelectedItem = event.value as DropdownItem | null;
      selectedDropdownItem.value = fullSelectedItem;

      let emittedValue: SelectedValue[] = [];
      if (fullSelectedItem && typeof fullSelectedItem === 'object' && props.optionValueKey in fullSelectedItem) {
        emittedValue = [fullSelectedItem[props.optionValueKey] as SelectedValue];
      } else if (fullSelectedItem) {
        emittedValue = [fullSelectedItem as unknown as SelectedValue];
      }

      emit('update:modelValue', emittedValue);
      emit('change', { originalEvent: event.originalEvent, value: emittedValue });
    };

    const optionGroupLabelKey = computed(() => props.optionGroupLabel);
    const optionGroupChildrenKey = computed(() => props.optionGroupChildren);

    const onBeforeShow = (event?: { target?: { overlay?: HTMLElement } }) => {
      setTimeout(() => {
        const overlayElement = event?.target?.overlay;
        if (overlayElement) {
          overlayElement.style.zIndex = String(parseInt(getComputedStyle(overlayElement).zIndex || '1000', 10) + 1);
        }
      }, 0);
    };

    return {
      selectedDropdownItem,
      handleChange,
      optionGroupLabelKey,
      optionGroupChildrenKey,
      onBeforeShow,
    };
  },
});
</script>

<style lang="scss" scoped>
.select-wrapper {
  width: 100%;
  display: flex;
}

.base-dropdown-multiple-group strong {
  font-weight: bold;
  display: block;
  padding: 0.5rem 0.75rem;
}

.base-dropdown-multiple-option {
  /* Custom styles for options if needed */
}

:deep(.p-dropdown) {
  width: 100%;
}
</style>
